import { useQuery } from '@apollo/client/react';
import { Badge, Card, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { QueryState } from '../components/QueryState';
import { AdminStatsDocument } from '../graphql/operations';
import { STORY_STATUS_COLORS, useLabels } from '../labels';

export function DashboardPage() {
  const { t } = useTranslation();
  const labels = useLabels();
  const { data, loading, error } = useQuery(AdminStatsDocument, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <Stack gap="lg">
      <Title order={2}>{t('dashboard.title')}</Title>

      <QueryState loading={loading && !data} error={error}>
        {data && (
          <>
            <Section title={t('dashboard.users')}>
              <StatCard label={t('dashboard.total')} value={data.adminStats.usersTotal} />
              <StatCard label={t('dashboard.plus')} value={data.adminStats.usersPlus} />
              <StatCard label={t('dashboard.today')} value={data.adminStats.usersToday} />
              <StatCard label={t('dashboard.week')} value={data.adminStats.usersWeek} />
            </Section>

            <Section title={t('dashboard.stories')}>
              <StatCard label={t('dashboard.total')} value={data.adminStats.storiesTotal} />
              <StatCard label={t('dashboard.today')} value={data.adminStats.storiesToday} />
              <StatCard label={t('dashboard.week')} value={data.adminStats.storiesWeek} />
            </Section>

            <Stack gap="xs">
              <Title order={4}>{t('dashboard.byStatus')}</Title>
              <Group gap="sm">
                {data.adminStats.storiesByStatus.map((row) => (
                  <Badge
                    key={row.status}
                    size="lg"
                    variant="light"
                    color={STORY_STATUS_COLORS[row.status]}
                  >
                    {labels.storyStatus(row.status)}: {row.count}
                  </Badge>
                ))}
              </Group>
            </Stack>
          </>
        )}
      </QueryState>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Title order={4}>{title}</Title>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="md">
        {children}
      </SimpleGrid>
    </Stack>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card withBorder padding="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fz={32} fw={700} lh={1.2} mt={4}>
        {value}
      </Text>
    </Card>
  );
}
